import { createAsyncThunk } from "@reduxjs/toolkit";
import { AuthService } from "@root/services/auth";
import { LocalStorageService } from "@services/local-storage";
import {
  AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_SIGNUP,
  AUTH_FULL_SIGNUP,
  DELETE_ACCOUNT,
  RESET_PASS,
  SET_NEW_PASSWORD,
  VERIFY_RESET_PASS,
  LANGUAGE_UPDATE,
} from "../action-types";
import { showAlert } from "../slices/features/alert";
import { getBaseUrl } from "../selectors/features";
import { TOAST_VARIANT } from "@root/constants/common";
import { t } from "i18next";
import { changeAppLanguage } from "@root/utils/change-language";
import { LANGUAGE } from "@root/constants";

const authService = new AuthService();
const localStorageService = new LocalStorageService();

interface LoginPayload {
  payload: Record<string, string>;
  onSuccess: any;
  onError: any;
  isSocial: boolean;
}

export const login = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_LOGIN,
  async (requestPayload: LoginPayload, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { onSuccess, onError, payload, isSocial } = requestPayload;
    const { data, error } = await authService.signIn(
      baseUrl,
      payload,
      isSocial
    );

    if (error) {
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError(error);
      return thunkAPI.rejectWithValue(data);
    } else {
      // Backend /api/auth/* returns { user, accessToken }
      const token = data?.token ?? data?.accessToken;
      await localStorageService.persist("authToken", token);
      token && onSuccess({ ...data, token });
    }

    thunkAPI.dispatch(
      showAlert({
        message: t("common:profile.loginSucessFull"),
        type: TOAST_VARIANT.SUCCESS,
      })
    );

    return thunkAPI.fulfillWithValue(data);
  }
);

export const updateLanguage = createAsyncThunk<
  TObject,
  { language: "en" | "ko" },
  IActionOptions
>(LANGUAGE_UPDATE, async ({ language }, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const token = await localStorageService.fetch("authToken");

  try {
    if (!token) {
      // no token found in localStorage
      return thunkAPI.rejectWithValue("No auth token found");
    }
    const { data, error } = await authService.updateLanguage(
      baseUrl,
      language,
      token
    );

    if (error) {
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      return thunkAPI.rejectWithValue(data);
    } 

    thunkAPI.dispatch(
      showAlert({
        message: t("common:language.languageUpdated"), 
        type: TOAST_VARIANT.SUCCESS,
      })
    );

    return thunkAPI.fulfillWithValue(data);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const adminLogin = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_LOGIN,
  async (requestPayload: LoginPayload, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { onSuccess, onError, payload, isSocial } = requestPayload;
    const { data, error } = await authService.signInAdmin(
      baseUrl,
      payload,
      isSocial
    );

    if (error) {
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError(error);
      return thunkAPI.rejectWithValue(data);
    } else {
      const { token } = data;
      await localStorageService.persist("authToken", token);
      token && onSuccess(data);
    }

    thunkAPI.dispatch(
      showAlert({
        message: t("common:profile.loginSucessFull"),
        type: TOAST_VARIANT.SUCCESS,
      })
    );

    return thunkAPI.fulfillWithValue(data);
  }
);

export const logout = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_LOGOUT,
  async (requestPayload: Record<string, string>, thunkAPI) => {
    const response = await authService.signOut();
    changeAppLanguage(LANGUAGE.KOREAN);
    thunkAPI.fulfillWithValue({ payload: requestPayload });
    return response?.data;
  }
);

export const signup = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_SIGNUP,
  async (requestPayload: Record<any, any>, thunkAPI) => {
    const { payload, onError, onSuccess } = requestPayload;
    const baseUrl = getBaseUrl(thunkAPI.getState());

    const response = await authService.signUp(baseUrl, payload);

    if (response?.error) {
      onError(response);
      thunkAPI.dispatch(
        showAlert({
          message:
            response?.data?.message ||
            response?.data?.userMessage ||
            response?.data?.code ||
            response?.data,
          type: TOAST_VARIANT.ERROR,
        })
      );
    } else {
      onSuccess(response);
      thunkAPI.dispatch(
        showAlert({
          message:
            response?.statusMessage || t("common:profile.signupSuccessfull"),
          type: TOAST_VARIANT.SUCCESS,
        })
      );
    }
  }
);
export const fullSignup = createAsyncThunk<TObject, TObject, IActionOptions>(
  AUTH_FULL_SIGNUP,
  async (requestPayload: Record<any, any>, thunkAPI) => {
    const { payload, token, onError, onSuccess } = requestPayload;

    const baseUrl = getBaseUrl(thunkAPI.getState());
    const response = await authService.fullSignUp(baseUrl, payload, token);

    if (response?.error) {
      onError(response);
      thunkAPI.dispatch(
        showAlert({
          message:
            response?.data?.message ||
            response?.data?.userMessage ||
            response?.data?.code ||
            response?.data,
          type: TOAST_VARIANT.ERROR,
        })
      );
    } else {
      onSuccess(response);
      thunkAPI.dispatch(
        showAlert({
          message:
            response?.statusMessage || t("common:profile.signupSuccessfull"),
          type: TOAST_VARIANT.SUCCESS,
        })
      );
    }
  }
);

export const resetPass = createAsyncThunk<TObject, TObject, IActionOptions>(
  RESET_PASS,
  async (requestPayload: any, thunkAPI) => {
    const { payload, onSuccess, onError } = requestPayload;
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const response = await authService.resetPass(baseUrl, payload);

    if (response?.error) {
      thunkAPI.dispatch(
        showAlert({
          message: response?.data || t("common:feedback.somethingWrong"),
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError();
      return thunkAPI.rejectWithValue({ payload: requestPayload });
    } else {
      thunkAPI.dispatch(
        showAlert({
          message: response?.statusMessage,
          type: TOAST_VARIANT.SUCCESS,
        })
      );
      onSuccess();
      return thunkAPI.fulfillWithValue({ payload: requestPayload });
    }
  }
);

export const verifyResetPass = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(VERIFY_RESET_PASS, async (requestPayload: any, thunkAPI) => {
  const { payload, onSuccess } = requestPayload;
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const response = await authService.verifyResetPass(baseUrl, payload);

  if (response?.error) {
    return thunkAPI.rejectWithValue({ payload: requestPayload });
  } else {
    onSuccess();

    return thunkAPI.fulfillWithValue({ payload: requestPayload });
  }
});

export const setNewPassword = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(SET_NEW_PASSWORD, async (requestPayload: any, thunkAPI) => {
  const { payload, onSuccess, onError } = requestPayload;
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const response = await authService.setNewPassword(baseUrl, payload);

  if (response?.error) {
    onError(response);
    return thunkAPI.rejectWithValue({ payload: requestPayload });
  } else {
    thunkAPI.dispatch(
      showAlert({
        message: response?.message || t("common:subscriptionSuccess.success"),
        type: TOAST_VARIANT.SUCCESS,
      })
    );
    onSuccess(response);
    return thunkAPI.fulfillWithValue({ payload: requestPayload });
  }
});

export const deleteAccount = createAsyncThunk<TObject, TObject, IActionOptions>(
  DELETE_ACCOUNT,
  async (requestPayload: { onSuccess: any; onError: any }, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { onSuccess, onError } = requestPayload;
    const { data, error } = await authService.deleteAccount(baseUrl);

    if (error) {
      thunkAPI.dispatch(
        showAlert({
          message:
            data?.message || data?.userMessage || data?.code || data || "",
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError(error);
      return thunkAPI.rejectWithValue(data);
    } else {
      thunkAPI.dispatch(
        showAlert({
          message: t("common:profile.deleteAccountSuccess"),
          type: TOAST_VARIANT.SUCCESS,
        })
      );

      onSuccess(data);
      return thunkAPI.fulfillWithValue(data);
    }
  }
);

export const resendEmail = createAsyncThunk<TObject, TObject, IActionOptions>(
  RESET_PASS,
  async (requestPayload: any, thunkAPI) => {
    const { payload, onSuccess, onError } = requestPayload;
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const response = await authService.resendEmail(baseUrl, payload);

    if (response?.error) {
      thunkAPI.dispatch(
        showAlert({
          message: response?.data || t("common:msgs.someThingWrong"),
          type: TOAST_VARIANT.ERROR,
        })
      );
      onError();
      return thunkAPI.rejectWithValue({ payload: requestPayload });
    } else {
      thunkAPI.dispatch(
        showAlert({
          message: response?.statusMessage,
          type: TOAST_VARIANT.SUCCESS,
        })
      );
      onSuccess();
      return thunkAPI.fulfillWithValue({ payload: requestPayload });
    }
  }
);
